import { useState } from 'react';
import { X, Package } from 'lucide-react';
import { ItemCategory } from '@/types';

interface Props {
  onConfirm: (data: {
    item_category: ItemCategory;
    item_name: string;
    item_brand?: string;
    condition: string;
    quantity: number;
    acquisition_price?: number;
    notes?: string;
  }) => void;
  onCancel: () => void;
}

const CATEGORIES: { value: ItemCategory; label: string }[] = [
  { value: 'booster_pack', label: 'Booster Pack'  },
  { value: 'booster_box',  label: 'Booster Box'   },
  { value: 'bundle',       label: 'Bundle / Lot'  },
  { value: 'merch',        label: 'Merch'          },
];

const CONDITIONS = ['Mint', 'Near Mint', 'Lightly Played', 'Moderately Played', 'Heavily Played', 'Played', 'Poor'];

export default function AddBulkMerchModal({ onConfirm, onCancel }: Props) {
  const [category, setCategory] = useState<ItemCategory>('booster_pack');
  const [name, setName] = useState('');
  const [brand, setBrand] = useState('');
  const [condition, setCondition] = useState('Near Mint');
  const [quantity, setQuantity] = useState(1);
  const [price, setPrice] = useState('');
  const [notes, setNotes] = useState('');

  const canSubmit = !!name.trim();

  const handleSubmit = () => {
    if (!canSubmit) return;
    onConfirm({
      item_category: category,
      item_name: name.trim(),
      item_brand: brand.trim() || undefined,
      condition,
      quantity,
      acquisition_price: price ? Number(price) : undefined,
      notes: notes.trim() || undefined,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
      <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-zinc-800">
          <div className="flex items-center gap-2">
            <Package className="h-5 w-5 text-brand-500" />
            <h2 className="font-semibold text-gray-900 dark:text-zinc-100">Add to Bulk & Merch</h2>
          </div>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 dark:hover:text-zinc-300">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-2">Type</label>
            <div className="grid grid-cols-2 gap-2">
              {CATEGORIES.map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setCategory(value)}
                  className={`py-2 px-3 rounded-xl border-2 text-sm font-medium transition-all ${
                    category === value
                      ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20 text-brand-700 dark:text-brand-300'
                      : 'border-gray-200 dark:border-zinc-700 text-gray-600 dark:text-zinc-400 hover:border-gray-300'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-1">
              Item Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder={category === 'merch' ? 'e.g. Pikachu Plush 12"' : 'e.g. Scarlet & Violet Booster Box'}
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full border border-gray-200 dark:border-zinc-700 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-400 text-gray-900 dark:text-zinc-100 bg-white dark:bg-zinc-800"
            />
          </div>

          {/* Brand */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-1">Brand / Game</label>
            <input
              type="text"
              placeholder="e.g. Pokemon, Magic: The Gathering"
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
              className="w-full border border-gray-200 dark:border-zinc-700 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-400 text-gray-900 dark:text-zinc-100 bg-white dark:bg-zinc-800"
            />
          </div>

          {/* Condition + Quantity */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-1">Condition</label>
              <select
                value={condition}
                onChange={(e) => setCondition(e.target.value)}
                className="w-full border border-gray-200 dark:border-zinc-700 rounded-xl px-3 py-3 focus:outline-none focus:ring-2 focus:ring-brand-400 text-gray-900 dark:text-zinc-100 bg-white dark:bg-zinc-800"
              >
                {CONDITIONS.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-1">Quantity</label>
              <input
                type="number"
                min={1}
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, Number(e.target.value)))}
                className="w-full border border-gray-200 dark:border-zinc-700 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-400 text-gray-900 dark:text-zinc-100 bg-white dark:bg-zinc-800"
              />
            </div>
          </div>

          {/* Price paid */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-1">Price Paid (optional)</label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">$</span>
              <input
                type="number"
                placeholder="0.00"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                className="w-full pl-8 pr-4 py-3 border border-gray-200 dark:border-zinc-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-400 text-gray-900 dark:text-zinc-100 bg-white dark:bg-zinc-800"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-1">Notes (optional)</label>
            <textarea
              rows={2}
              placeholder="Any details about this item..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full border border-gray-200 dark:border-zinc-700 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-400 resize-none text-gray-900 dark:text-zinc-100 bg-white dark:bg-zinc-800"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-5 pb-5">
          <button onClick={onCancel} className="btn-outline flex-1 py-2.5">Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="btn-primary flex-1 py-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Add to Collection
          </button>
        </div>
      </div>
    </div>
  );
}
